import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class DetailsPageResolver implements Resolve<any> {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  private useTransferState<T>(
    key: string,
    observable$: Observable<T>,
  ): Observable<T> {
    const STATE_KEY = makeStateKey<T>(key);

    if (this.transferState.hasKey(STATE_KEY)) {
      return of(this.transferState.get<T>(STATE_KEY, null as any));
    }

    return observable$.pipe(
      map((data) => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(STATE_KEY, data);
        }
        return data;
      }),
    );
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const lang = route.url[0]?.path;
    const pagename = route.url[2]?.path;
    const filename = route.url[3]?.path;
    const title = route.url[4]?.path;

    if (lang && pagename && filename && title) {
      const basePath = `/assets/${environment.projectName}/content/${lang}`;

      const contentUrl = `${basePath}/${pagename}/${filename}.json`;
      const metadataUrl = `${basePath}/meta-data.json`;

      const reviewsUrl = `${basePath}/home-page/reviews.json`;
      const coursesUrl = `${basePath}/service-page/courses.json`;

      return forkJoin({
        content: this.useTransferState(
          `details-${lang}-${pagename}-${filename}-${title}`,
          this.http.get<any>(contentUrl).pipe(
            map((data) => {
              const article = data?.services?.find(
                (service: any) =>
                  service.title.toLowerCase().replace(/ /g, '-') ===
                  title.toLowerCase(),
              );
              return article || null;
            }),
            catchError(() => of(null)),
          ),
        ),

        metadata: this.useTransferState(
          `details-meta-${lang}-${pagename}`,
          this.http.get<any>(metadataUrl).pipe(catchError(() => of(null))),
        ),

        reviews: this.useTransferState(
          `details-${lang}-${pagename}`,
          this.http.get<any>(reviewsUrl).pipe(catchError(() => of(null))),
        ),

        courses: this.useTransferState(
          `details-${lang}-${pagename}`,
          this.http.get<any>(coursesUrl).pipe(catchError(() => of(null))),
        ),
      }).pipe(
        map(({ content, metadata, reviews, courses }) => ({
          content,
          metadata,
          reviews,
          courses,
        })),
      );
    } else {
      return of(null);
    }
  }
}
